// ledgerCalc.js — rebuilds each calculated ledger figure from the rows the page already holds, so a hover can show HOW
// the number was reached. Mirrors backend/ledger/csvs.py totals() + services/v4_bridge.py _metrics():
//   called / distributed = sum of the call / dist rows (adjustment rows included), none when the leaf has no row of that type
//   value                = the latest value of each position, summed
//   DPI = dist / called, TVPI = (dist + value) / called, RVPI = value / called (null when nothing was called)
//   XIRR                 = every row not marked xirr=no, plus each position's latest non-zero value on its date
// Input rows are GET /api/ledger/timeline points: {date, kind: call|distribution|value, amount, flag, xirr, position_id}.
// Optional per point: `pos` (unique position identity when pooling several leaves) and `label` (shown in the text).

export const MONEY_TOL = 1;
export const RATIO_TOL = 1e-4;
const LIST_MAX = 12;
const EDGE = 5;

const day = (d) => String(d || "").slice(0, 10);
const posOf = (p) => p.pos ?? p.position_id ?? "";
const kindOf = (p) => (p.kind === "dist" ? "distribution" : p.kind);

// Same algorithm as services/xirr.py (Newton from 0.1, bisection fallback). Flows: [[YYYY-MM-DD, amount]].
function xnpv(rate, amounts, days) {
  if (rate <= -1) return Infinity;
  return amounts.reduce((s, cf, i) => s + cf / (1 + rate) ** (days[i] / 365), 0);
}
function xnpvDeriv(rate, amounts, days) {
  if (rate <= -1) return 0;
  return amounts.reduce((s, cf, i) => s + (-cf * (days[i] / 365)) / (1 + rate) ** (days[i] / 365 + 1), 0);
}
export function xirr(flows) {
  if (flows.length < 2) return null;
  const items = flows.map(([d, a]) => [Date.parse(`${day(d)}T00:00:00Z`), Number(a)]).sort((a, b) => a[0] - b[0]);
  if (!(items.some(([, a]) => a > 0) && items.some(([, a]) => a < 0))) return null;
  const days = items.map(([t]) => Math.round((t - items[0][0]) / 86400000));
  const amounts = items.map(([, a]) => a);
  const tol = 1e-7;
  let rate = 0.1;
  for (let i = 0; i < 100; i += 1) {
    const fx = xnpv(rate, amounts, days);
    if (Math.abs(fx) < tol) return rate;
    const dfx = xnpvDeriv(rate, amounts, days);
    if (dfx === 0 || !(dfx > -1e10 && dfx < 1e10)) break;
    let next = rate - fx / dfx;
    if (next <= -0.999999) next = (rate - 0.999999) / 2;
    if (Math.abs(next - rate) < tol) { rate = next; break; }
    rate = next;
    if (!(rate > -0.999999 && rate < 1e6)) break;
  }
  if (Math.abs(xnpv(rate, amounts, days)) < 1e-3) return rate;
  let lo = -0.999, hi = 100;
  let flo = xnpv(lo, amounts, days);
  if (flo * xnpv(hi, amounts, days) > 0) return null;
  for (let i = 0; i < 200; i += 1) {
    const mid = (lo + hi) / 2;
    const fmid = xnpv(mid, amounts, days);
    if (Math.abs(fmid) < tol) return mid;
    if (flo * fmid < 0) hi = mid; else { lo = mid; flo = fmid; }
    if (hi - lo < tol) return (lo + hi) / 2;
  }
  return null;
}

// The figures the backend would compute from these rows.
export function totalsFromPoints(points) {
  const calls = points.filter((p) => kindOf(p) === "call");
  const dists = points.filter((p) => kindOf(p) === "distribution");
  const last = new Map();
  points
    .filter((p) => kindOf(p) === "value")
    .slice()
    .sort((a, b) => (day(a.date) < day(b.date) ? -1 : day(a.date) > day(b.date) ? 1 : 0))
    .forEach((p) => last.set(posOf(p), p));
  const tips = [...last.values()];
  const sum = (rows) => (rows.length ? rows.reduce((s, p) => s + Number(p.amount), 0) : null);
  const called = sum(calls);
  const distributed = sum(dists);
  const value = tips.length ? tips.reduce((s, p) => s + Number(p.amount), 0) : null;
  const flows = [
    ...[...calls, ...dists].filter((p) => p.xirr !== "no")
      .map((p) => ({ date: day(p.date), amount: kindOf(p) === "call" ? -Number(p.amount) : Number(p.amount), kind: kindOf(p) })),
    ...tips.filter((p) => Number(p.amount)).map((p) => ({ date: day(p.date), amount: Number(p.amount), kind: "value" })),
  ].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
  const ok = flows.some((f) => f.amount < 0) && flows.some((f) => f.amount > 0) && new Set(flows.map((f) => f.date)).size > 1;
  const ratio = (n) => (n == null || !called ? null : n / called);
  return {
    calls, dists, tips, flows, called, distributed, value,
    dpi: ratio(distributed),
    tvpi: ratio((distributed ?? 0) + (value ?? 0)),
    rvpi: ratio(value),
    xirr: ok ? xirr(flows.map((f) => [f.date, f.amount])) : null,
  };
}

const pct = (v, dp = 1) => `${(Number(v) * 100).toFixed(dp)}%`;

function rowTerms(rows, word, fmt) {
  const multi = new Set(rows.map((p) => p.label ?? "")).size > 1;
  return rows.map((p) => {
    const notes = [];
    if (String(p.flag || "").includes("computed")) notes.push("computed adjustment");
    if (p.xirr === "no") notes.push("not in XIRR");
    return `${multi && p.label ? `${p.label} ` : ""}${fmt(Number(p.amount))} (${day(p.date)} ${word}${notes.length ? `, ${notes.join(", ")}` : ""})`;
  });
}
const sumLine = (head, terms, total) => `${head} = ${terms.join(" + ")}${terms.length > 1 ? ` = ${total}` : ""}`;

function flowLine(f, fmt) {
  return `${fmt(f.amount)} (${f.date} ${f.kind === "call" ? "call" : f.kind === "value" ? "value" : "distribution"})`;
}

// metric: called | distributed | value | dpi | tvpi | rvpi | xirr.  shown: the number the page displays (raw, may be null).
// Returns {lines: string[], text, calc, shown, differs}.
export function explain(metric, points, shown, fmt) {
  const t = totalsFromPoints(points || []);
  const lines = [];
  const money = (n) => (n == null ? "—" : fmt(n));
  const ratioTxt = (n) => (n == null ? "—" : n.toFixed(2));
  let calc;
  let ratio = true;
  switch (metric) {
    case "called":
      ratio = false; calc = t.called;
      lines.push(t.calls.length ? sumLine("Called", rowTerms(t.calls, "call", fmt), money(calc)) : "Called = — (no call rows)");
      break;
    case "distributed":
      ratio = false; calc = t.distributed;
      lines.push(t.dists.length ? sumLine("Distributed", rowTerms(t.dists, "distribution", fmt), money(calc)) : "Distributed = — (no distribution rows)");
      break;
    case "value": {
      ratio = false; calc = t.value;
      if (!t.tips.length) { lines.push("Value = — (no value rows)"); break; }
      const named = t.tips.length > 1;
      const terms = t.tips.map((p) => `${named ? `${p.label ?? (posOf(p) || "unkeyed")} ` : ""}${fmt(Number(p.amount))} (${day(p.date)})`);
      lines.push(`Value = latest value per position: ${terms.join(" + ")}${terms.length > 1 ? ` = ${money(calc)}` : ""}`);
      break;
    }
    case "dpi":
      calc = t.dpi;
      lines.push(t.called && t.distributed != null ? `DPI = distributed ${money(t.distributed)} / called ${money(t.called)} = ${ratioTxt(calc)}` : "DPI = — (needs called capital and distributions)");
      break;
    case "tvpi":
      calc = t.tvpi;
      lines.push(t.called
        ? `TVPI = (distributed ${money(t.distributed ?? 0)} + value ${money(t.value ?? 0)}) / called ${money(t.called)} = ${ratioTxt(calc)}`
        : "TVPI = — (no called capital)");
      break;
    case "rvpi":
      calc = t.rvpi;
      lines.push(t.called && t.value != null ? `RVPI = value ${money(t.value)} / called ${money(t.called)} = ${ratioTxt(calc)}` : "RVPI = — (needs called capital and a value)");
      break;
    case "xirr": {
      calc = t.xirr;
      ratio = true;
      const n = t.flows.length;
      if (calc == null) {
        lines.push(`XIRR = — (${n} flow${n === 1 ? "" : "s"}; needs a call and a positive flow on at least two dates)`);
      } else {
        const hasValue = t.flows.some((f) => f.kind === "value");
        lines.push(`XIRR over ${n} flows from ${t.flows[0].date} to ${t.flows[n - 1].date}${hasValue ? ", value as terminal flow" : ""} = ${pct(calc)}`);
      }
      if (n <= LIST_MAX) t.flows.forEach((f) => lines.push(flowLine(f, fmt)));
      else {
        t.flows.slice(0, EDGE).forEach((f) => lines.push(flowLine(f, fmt)));
        lines.push(`… ${n - 2 * EDGE} more flows …`);
        t.flows.slice(n - EDGE).forEach((f) => lines.push(flowLine(f, fmt)));
      }
      break;
    }
    default:
      return null;
  }
  const tol = ratio ? RATIO_TOL : MONEY_TOL;
  const a = shown == null ? null : Number(shown);
  const differs = (a == null) !== (calc == null) || (a != null && calc != null && Math.abs(a - calc) > tol);
  if (differs) {
    const f = (v) => (v == null ? "—" : metric === "xirr" ? pct(v, 2) : ratio ? v.toFixed(4) : fmt(v));
    lines.push(`differs: page shows ${f(a)}, these rows give ${f(calc)}`);
  }
  return { lines, text: lines.join("\n"), calc, shown: a, differs };
}
